import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CustomerService } from "services/CustomerService";
import { GetApiKeyDto } from "services/Dtos/ApiKeyDtos";
import ExportButton from "components/ExportButton";
import ApiKeySidebar from "components/Sidebars/ApiKeySidebar";
import Drawer, { FilterDrawer } from "components/common/Drawer";
import Pagination from "components/common/Pagination";
import { useListParams } from "hooks/useListParams";
import { forgetApiKeyName } from "hooks/useApiKeyNames";
import { useCompanyContext } from "./CompanyContext";

const PAGE_SIZE = 10;

const emptyFilters = { name: "", email: "", phone: "", description: "" };

const filterFields: { key: keyof typeof emptyFilters; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "description", label: "Description" },
];

const CompanyApiKeys: React.FC = () => {
  const { companyId } = useCompanyContext();
  const { pageNumber, setPageNumber, appliedFilters, setAppliedFilters } = useListParams(emptyFilters);

  const [apiKeys, setApiKeys] = useState<GetApiKeyDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  // Key whose settings are open in the side panel.
  const [editingKey, setEditingKey] = useState<GetApiKeyDto | null>(null);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilters);
  const activeFilterCount = Object.values(appliedFilters).filter((v) => v !== "").length;

  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  const fetchApiKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await CustomerService.customer.getCustomerApiKeys(
        companyId,
        pageNumber,
        PAGE_SIZE,
        appliedFilters.name || undefined,
        appliedFilters.email || undefined,
        appliedFilters.phone || undefined,
        appliedFilters.description || undefined,
      );
      setApiKeys(response.data.apiKeys ?? []);
      setTotalCount(response.data.totalCount ?? 0);
    } catch (err) {
      console.error("Failed to load API keys:", err);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, pageNumber, appliedFilters]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleExport = async (format: string) => {
    setIsExporting(true);
    try {
      const response = await CustomerService.customer.exportCustomerApiKeys(
        companyId,
        appliedFilters.name || undefined,
        appliedFilters.email || undefined,
        appliedFilters.phone || undefined,
        appliedFilters.description || undefined,
        format,
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `api-keys.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      console.error("Failed to export API keys");
    } finally {
      setIsExporting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("API key copied to clipboard!", { autoClose: 3000 }))
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy API key", { autoClose: 3000 });
      });
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <h3 className="mb-0">API Keys</h3>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setIsFilterOpen(true)} disabled={isLoading}>
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && <span className="badge bg-primary ms-1">{activeFilterCount}</span>}
          </button>
          <ExportButton onExport={handleExport} isExporting={isExporting} size="sm" />
          <button className="btn btn-outline-primary btn-sm" onClick={fetchApiKeys} disabled={isLoading}>
            <i className={`${isLoading ? "spinner-border spinner-border-sm" : "bi bi-arrow-clockwise"} me-1`}></i>
            Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setIsAddOpen(true)}>
            <i className="bi bi-plus-circle me-1"></i>Add API Key
          </button>
        </div>
      </div>

      <FilterDrawer
        title="Filter API Keys"
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onClear={() => {
          setTempFilters(emptyFilters);
          setAppliedFilters(emptyFilters);
        }}
        onApply={() => {
          setAppliedFilters({ ...tempFilters });
          setIsFilterOpen(false);
        }}
      >
        {filterFields.map(({ key, label }) => (
          <div className="mb-4" key={key}>
            <label className="form-label fw-bold small text-muted">{label}</label>
            <div className="input-group">
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input
                type="text"
                className="form-control"
                placeholder={`Filter by ${label.toLowerCase()}...`}
                value={tempFilters[key]}
                onChange={(e) => setTempFilters((f) => ({ ...f, [key]: e.target.value }))}
              />
            </div>
          </div>
        ))}
      </FilterDrawer>

      <Drawer isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} title="Add API Key" icon="plus-circle" width={480}>
        <ApiKeySidebar
          customerId={companyId}
          onClose={() => setIsAddOpen(false)}
          onSave={fetchApiKeys}
        />
      </Drawer>

      <Drawer isOpen={!!editingKey} onClose={() => setEditingKey(null)} title="Key Settings" icon="gear" width={480}>
        {editingKey && (
          <ApiKeySidebar
            key={editingKey.id}
            apiKeyId={editingKey.id}
            customerId={companyId}
            onClose={() => setEditingKey(null)}
            onSave={() => {
              forgetApiKeyName(editingKey.id);
              fetchApiKeys();
            }}
          />
        )}
      </Drawer>

      {isLoading && apiKeys.length === 0 ? (
        <div className="d-flex justify-content-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="alert alert-info">
          {activeFilterCount > 0 ? "No API keys match your search criteria." : "No API keys associated with this company."}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>API Key</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Description</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((apiKey) => (
                <tr key={apiKey.id}>
                  <td>{apiKey.id}</td>
                  <td className="fw-medium">{apiKey.name}</td>
                  <td>
                    <div className="input-group input-group-sm">
                      <input type="text" className="form-control form-control-sm" value={apiKey.key} readOnly aria-label="API Key" />
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={() => copyToClipboard(apiKey.key)}
                        title="Copy to clipboard"
                      >
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                  </td>
                  <td>{apiKey.phone || "-"}</td>
                  <td>{apiKey.email || "-"}</td>
                  <td
                    title={apiKey.description || undefined}
                    style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {apiKey.description || "-"}
                  </td>
                  <td className="text-end text-nowrap">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setEditingKey(apiKey)}
                      title="Key settings"
                    >
                      Settings
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination pageNumber={pageNumber} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={setPageNumber} />
        </div>
      )}
    </div>
  );
};

export default CompanyApiKeys;
